<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

const props = defineProps<{
  title?: string,
  label?: string,
  position?: "left" | "right",
  fullWidth?: boolean,
}>();

const dropdownEl = ref<HTMLElement | null>(null);
const isOpen = ref(false);

function toggleDropdown() {
  isOpen.value = !isOpen.value;
}
function checkClickTarget(e: MouseEvent) {
  if (!isOpen.value) return;
  if (dropdownEl.value && !e.composedPath().includes(dropdownEl.value)) {
    isOpen.value = false;
  }
}

onMounted(() => {
  document.body.addEventListener("click", checkClickTarget);
});
onBeforeUnmount(() => {
  document.body.removeEventListener("click", checkClickTarget);
});
</script>

<template>
  <div ref="dropdownEl" class="dropdown">
    <div v-if="props.label" class="label">{{ props.label }}</div>
    <div tabindex="0" class="trigger" @click="toggleDropdown">
      <template v-if="props.title">
        {{ props.title }}
      </template>

      <span class="icon">
        <slot name="icon">
          <FontAwesomeIcon :icon="faChevronDown" />
        </slot>
      </span>
    </div>
    
    <Transition name="fade">
      <div v-if="isOpen" :class="['content', { fullwidth: props.fullWidth, right: props.position === 'right' }]" @click.stop>
        <slot />
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
.dropdown {
  user-select: none;
  position: relative;
  .label {
    font-weight: map.get(v.$font-weight, "medium");
    color: map.get(v.$gray, "700");
    font-size: .875rem;
    margin-bottom: 5px;
  }
  .trigger {
    cursor: pointer;
    line-height: 140%;
    font-weight: map.get(v.$font-weight, "medium");
    padding: 10px 14px;
    border-radius: map.get(v.$border-radius, "max");
    border: 1px solid map.get(v.$gray, "300");
    outline-color: map.get(v.$primary, "50");
    background-color: white;
    transition: .2s;
    box-shadow: map.get(v.$shadow, "xs");
    display: flex;
    gap: 10px;
    justify-content: space-between;
    align-items: center;
    &:focus {
      border-color: map.get(v.$primary, "300");
      outline: 3px solid map.get(v.$primary, "50");
    }
    .icon {
      color: map.get(v.$gray, "400");
    }
  }
  .content {
    position: absolute;
    min-width: 100%;
    top: calc(100% + 8px);
    z-index: 2;
    &.fullwidth {
      // TODO dynamic width calculation instead of right
      right: 0;
      width: calc(100dvw - 30px);
    }
    &.right {
      right: 0;
    }
  }
}
</style>